import { supabase } from './supabase/client';
import { invokeAdminUsers } from './supabase/adminUsers';
import {
  Student,
  CreateStudentDTO,
  UpdateStudentDTO,
  StudentFilters,
  StudentListResponse,
} from 'src/types/student.types';

const TABLE_NAME = 'students';

async function resolveTeacherId(directId?: string, userId?: string): Promise<string | null> {
  if (directId) return directId;
  if (!userId) return null;
  const { data } = await supabase
    .from('teachers')
    .select('id, userid')
    .or(`userid.eq.${userId},id.eq.${userId}`)
    .maybeSingle();
  return data?.id || null;
}

async function fetchAssignedStudentIdsFromTeacherId(teacherId: string): Promise<string[]> {
  const [cohortsRes, classesRes] = await Promise.all([
    supabase.from('teacher_cohorts').select('cohort_id').eq('teacher_id', teacherId),
    supabase.from('classes').select('id').eq('teacherid', teacherId),
  ]);

  const cohortIds = (cohortsRes.data || []).map((tc: any) => tc.cohort_id).filter(Boolean);
  const classIds = (classesRes.data || []).map((c: any) => c.id).filter(Boolean);

  const [cohortEnrollments, classEnrollments] = await Promise.all([
    cohortIds.length > 0
      ? supabase.from('student_cohort_enrollments').select('student_id').in('cohort_id', cohortIds)
      : Promise.resolve({ data: [] }),
    classIds.length > 0
      ? supabase.from('studentclassenrollments').select('studentid').in('classid', classIds)
      : Promise.resolve({ data: [] }),
  ]);

  return Array.from(
    new Set([
      ...(cohortEnrollments.data || []).map((e: any) => e.student_id),
      ...(classEnrollments.data || []).map((e: any) => e.studentid),
    ])
  );
}

/**
 * Helper to resolve assigned student IDs for a teacher across both
 * cohorts (teacher_cohorts -> student_cohort_enrollments) and
 * legacy classes (classes.teacherid -> studentclassenrollments).
 */
export async function resolveAssignedStudentIdsForTeacher(params: {
  teacherId?: string;
  teacherUserId?: string;
  scope?: string;
}): Promise<string[] | null> {
  if (params.scope === 'all' || (!params.teacherId && !params.teacherUserId)) {
    return null;
  }

  const teacherId = await resolveTeacherId(params.teacherId, params.teacherUserId);
  if (teacherId) return fetchAssignedStudentIdsFromTeacherId(teacherId);
  return params.teacherUserId ? fetchBankerStudentIds(params.teacherUserId) : [];
}

/**
 * A Banker has no teacher record. Their scope is the cohorts they are
 * enrolled in as a student, minus themselves, so they never pay or approve
 * loans for their own account.
 */
async function fetchBankerStudentIds(userId: string): Promise<string[]> {
  const { data: self } = await supabase.from('students').select('id').eq('userid', userId).maybeSingle();
  if (!self) return [];

  const { data: ownEnrollments } = await supabase
    .from('student_cohort_enrollments')
    .select('cohort_id')
    .eq('student_id', self.id);
  const cohortIds = (ownEnrollments || []).map((e: any) => e.cohort_id).filter(Boolean);
  if (cohortIds.length === 0) return [];

  const { data: classmates } = await supabase
    .from('student_cohort_enrollments')
    .select('student_id')
    .in('cohort_id', cohortIds);
  const ids = new Set((classmates || []).map((e: any) => e.student_id));
  ids.delete(self.id);
  return Array.from(ids);
}

export async function resolveScopedStudentIds(filters?: {
  cohortId?: string;
  teacherId?: string;
  teacherUserId?: string;
}): Promise<string[] | null> {
  if (filters?.cohortId && filters.cohortId !== 'ALL') {
    const { data: enrollments } = await supabase
      .from('student_cohort_enrollments')
      .select('student_id')
      .eq('cohort_id', filters.cohortId);
    return (enrollments || []).map((e: any) => e.student_id);
  }
  if (filters?.teacherId || filters?.teacherUserId) {
    return resolveAssignedStudentIdsForTeacher({
      teacherId: filters.teacherId,
      teacherUserId: filters.teacherUserId,
    });
  }
  return null;
}

function calculateTransactionTotals(items: any[]): {
  totalBalance: number;
  totalCredit: number;
  totalDebit: number;
} {
  let totalCredit = 0;
  let totalDebit = 0;

  items.forEach((item) => {
    if (item.status === 'Approved' || !item.status) {
      const amt = Number(item.amount) || 0;
      if (item.transaction_type?.type === 'credit') {
        totalCredit += amt;
      } else if (item.transaction_type?.type === 'debit') {
        totalDebit += amt;
      }
    }
  });

  return {
    totalBalance: totalCredit - totalDebit,
    totalCredit,
    totalDebit,
  };
}

function aggregateMonthlyBalances(items: any[]): { month: string; balance: number }[] {
  const monthlyMap: Record<string, number> = {};

  items.forEach((t) => {
    if (t.status === 'Approved' || !t.status) {
      const month = t.createdat ? t.createdat.substring(0, 7) : '';
      if (!month) return;
      if (!monthlyMap[month]) monthlyMap[month] = 0;
      const amt = Number(t.amount) || 0;
      if (t.transaction_type?.type === 'credit') {
        monthlyMap[month] += amt;
      } else if (t.transaction_type?.type === 'debit') {
        monthlyMap[month] -= amt;
      }
    }
  });

  return Object.keys(monthlyMap)
    .sort()
    .map((month) => ({
      month,
      balance: monthlyMap[month],
    }));
}

function parseTopPerformingStudentOptions(
  options?:
    | number
    | {
        limit?: number;
        teacherId?: string;
        teacherUserId?: string;
        cohortId?: string;
      }
) {
  if (typeof options === 'number') {
    return { limit: options, teacherId: undefined, teacherUserId: undefined, cohortId: undefined };
  }
  return {
    limit: options?.limit || 5,
    teacherId: options?.teacherId,
    teacherUserId: options?.teacherUserId,
    cohortId: options?.cohortId,
  };
}

/**
 * Service for managing student data
 */
export const studentService = {
  /**
   * Get students with filters and pagination
   */

  async getActiveStudents(filters?: {
    teacherId?: string;
    teacherUserId?: string;
  }): Promise<
    Array<{
      id: string;
      name: string;
      email: string;
      currentPoints: number;
      studentId: string;
    }>
  > {
    try {
      const assignedStudentIds = filters ? await resolveAssignedStudentIdsForTeacher(filters) : null;
      if (assignedStudentIds !== null && assignedStudentIds.length === 0) {
        return [];
      }

      let query = supabase
        .from('users')
        .select(
          `
          id,
          firstname,
          lastname,
          role,
          email,
          student:students!inner(
            id,
            balance
          )
        `
        )
        // students!inner already limits this to scholars, Bankers included.
        .is('deletedat', null);

      if (assignedStudentIds !== null) {
        query = query.in('student.id', assignedStudentIds);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (
        data?.map((user: any) => ({
          id: user.id,
          name: `${user.firstname} ${user.lastname}`,
          email: user.email,
          currentPoints: user.student?.balance || 0,
          studentId: user.student?.id,
        })) || []
      );
    } catch (error) {
      console.error('[StudentService.getActiveStudents]:', error);
      throw error;
    }
  },

  async getStudents({
    grade,
    search,
    cohortId,
    teacherId,
    teacherUserId,
    page = 1,
    limit = 10,
  }: StudentFilters = {}): Promise<StudentListResponse> {
    try {
      let query = supabase.from(TABLE_NAME).select('*, user:users!inner(*), badge:badges(*)', { count: 'exact' });

      query = query.is('user.deletedat', null);

      // 1. Teacher-scoped cohort/class filtering
      const assignedStudentIds = await resolveAssignedStudentIdsForTeacher({ teacherId, teacherUserId });
      if (assignedStudentIds !== null) {
        if (assignedStudentIds.length === 0) {
          return { data: [], total: 0, page, limit };
        }
        query = query.in('id', assignedStudentIds);
      }

      // 2. Explicit Cohort ID filtering
      if (cohortId) {
        const { data: enrollments } = await supabase
          .from('student_cohort_enrollments')
          .select('student_id')
          .eq('cohort_id', cohortId);

        const studentIds = (enrollments || []).map((e) => e.student_id);
        if (studentIds.length === 0) {
          return { data: [], total: 0, page, limit };
        }
        query = query.in('id', studentIds);
      }

      if (grade) {
        query = query.eq('grade', grade);
      }

      if (search && search.trim()) {
        const term = search.trim();
        // Commas, brackets and quotes are syntax inside .or(...), so a search like "Lee, Ann"
        // would split the filter. Strip them from everything that goes into it.
        const orSafe = (value: string) => value.replace(/[,()"\\]/g, ' ').replace(/\s+/g, ' ').trim();
        const words = orSafe(term).split(' ').filter(Boolean);

        // 1. Match students by external_student_id
        const { data: matchedStudents } = await supabase
          .from(TABLE_NAME)
          .select('id, userid')
          .ilike('external_student_id', `%${term}%`);

        // 2. Match users by first name, last name, email, or multi-word full name
        let userQuery = supabase
          .from('users')
          .select('id')
          .is('deletedat', null);

        if (words.length === 1) {
          userQuery = userQuery.or(
            `firstname.ilike.%${words[0]}%,lastname.ilike.%${words[0]}%,email.ilike.%${words[0]}%`
          );
        } else {
          userQuery = userQuery.or(
            `and(firstname.ilike.%${words[0]}%,lastname.ilike.%${words[1]}%),and(firstname.ilike.%${words[1]}%,lastname.ilike.%${words[0]}%),email.ilike.%${orSafe(term)}%`
          );
        }

        // A search of only punctuation leaves no words to match by name.
        const { data: matchedUsers } = words.length ? await userQuery : { data: [] };

        const userIds = Array.from(
          new Set([
            ...(matchedUsers || []).map((u) => u.id),
            ...(matchedStudents || []).map((s) => s.userid).filter(Boolean),
          ])
        );

        if (userIds.length === 0) {
          return { data: [], total: 0, page, limit };
        }

        query = query.in('userid', userIds);
      }

      // Add pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to).order('createdat', { ascending: false });

      const { data, error, count } = await query;

      if (error) {
        console.error('Query error:', error);
        throw error;
      }

      return {
        data: data as Student[],
        total: count || 0,
        page,
        limit,
      };
    } catch (error) {
      console.error('[StudentService.getStudents]:', error);
      throw error;
    }
  },

  /**
   * Get a single student by ID
   */
  async getStudentById(id: string): Promise<Student | null> {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('*, user:users(*),badge:badges(*)')
        .eq('id', id)
        .single();

      console.log('[StudentService.getStudentById]:', data);
      if (error) throw error;
      return data as Student;
    } catch (error) {
      console.error('[StudentService.getStudentById]:', error);
      throw error;
    }
  },

  /**
   * Create a new student with Supabase auth account
   */
  async createStudent(student: CreateStudentDTO): Promise<Student> {
    try {
      // The Edge Function creates the login, users row and students row, and undoes them on failure.
      return await invokeAdminUsers<Student>('create_student', {
        email: student.email,
        password: student.password,
        firstname: student.firstname,
        lastname: student.lastname,
        phonenumber: student.phonenumber,
        studentId: student.studentId,
        grade: student.grade,
      });
    } catch (error: any) {
      console.error('[StudentService.createStudent]:', error);

      // Format error message for display
      let errorMessage = 'Failed to create student';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.code === '22P02') {
        errorMessage = 'Invalid grade format';
      } else if (error.code === '23505') {
        errorMessage = 'A student with this email already exists';
      }

      throw new Error(errorMessage);
    }
  },

  /**
   * Update an existing student
   */
  async updateStudent(id: string, updates: UpdateStudentDTO): Promise<Student> {
    try {
      const student = await this.getStudentById(id);
      if (!student) throw new Error('Student not found');

      if (updates.email !== student.user.email) {
        await invokeAdminUsers('update_email', { userId: student.userid, email: updates.email });
      }

      // Update user record (keep extendedproperties.studentId in sync during transition)
      const nextExternalId =
        updates.studentId !== undefined
          ? updates.studentId
            ? String(updates.studentId).trim()
            : null
          : undefined;

      const { error: userError } = await supabase
        .from('users')
        .update({
          firstname: updates.firstname,
          lastname: updates.lastname,
          email: updates.email,
          deletedat: null,
          extendedproperties: {
            ...student.user.extendedproperties,
            phone: updates.phonenumber,
            ...(nextExternalId !== undefined ? { studentId: nextExternalId } : {}),
          },
        })
        .eq('id', student.userid);

      if (userError) throw userError;

      // Update student data
      const studentUpdatePayload: any = {
        updatedat: new Date().toISOString(),
      };
      if (nextExternalId !== undefined) studentUpdatePayload.external_student_id = nextExternalId;
      if (updates.grade !== undefined && updates.grade !== '') studentUpdatePayload.grade = +updates.grade;
      if (updates.balance !== undefined) studentUpdatePayload.balance = updates.balance;
      if (updates.status !== undefined) studentUpdatePayload.status = updates.status;

      const { data, error } = await supabase
        .from(TABLE_NAME)
        .update(studentUpdatePayload)
        .eq('id', id)
        .select('*, user:users(*)')
        .single();

      if (error) throw error;
      return data as Student;
    } catch (error) {
      console.error('[StudentService.updateStudent]:', error);
      throw error;
    }
  },

  /**
   * Delete a student
   */
  async deleteStudent(id: string): Promise<void> {
    try {
      // 1. Get student record
      const { data: student, error: fetchError } = await supabase
        .from(TABLE_NAME)
        .select('*, user:users(*)')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      if (!student) throw new Error('Student not found');

      const now = new Date().toISOString();

      // 2. Update user record with deletedat
      const { error: userError } = await supabase
        .from('users')
        .update({
          deletedat: now,
          updatedat: now,
        })
        .eq('id', student.userid);

      if (userError) throw userError;

      // 3. Update student record timestamp
      const { error: studentError } = await supabase
        .from(TABLE_NAME)
        .update({
          updatedat: now,
        })
        .eq('id', id);

      if (studentError) throw studentError;

      // 4. Soft-delete all transactions belonging to this student
      await supabase
        .from('transactions')
        .update({ deletedat: now })
        .eq('studentid', id)
        .is('deletedat', null);

      // 5. Soft-delete all loans belonging to this student
      await supabase
        .from('loans')
        .update({ deletedat: now })
        .eq('studentid', id)
        .is('deletedat', null);

      // 6. Soft-delete all student behaviours belonging to this student
      await supabase
        .from('student_behaviours')
        .update({ deletedAt: now })
        .eq('studentId', id)
        .is('deletedAt', null);

      // 7. Soft-delete financial challenges
      await supabase
        .from('student_financial_challenges')
        .update({ deletedat: now })
        .eq('studentid', id)
        .is('deletedat', null);

      // 8. Remove from active cohort enrollments
      await supabase
        .from('student_cohort_enrollments')
        .delete()
        .eq('student_id', id);
    } catch (error) {
      console.error('[StudentService.deleteStudent]:', error);
      throw error;
    }
  },

  /**
   * Update student balance
   */
  async updateBalance(id: string, amount: number): Promise<Student> {
    try {
      const student = await this.getStudentById(id);
      if (!student) throw new Error('Student not found');

      const newBalance = (student.balance || 0) + amount;

      const { data, error } = await supabase
        .from(TABLE_NAME)
        .update({
          balance: newBalance,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select('*, user:users(*)')
        .single();

      if (error) throw error;
      return data as Student;
    } catch (error) {
      console.error('[StudentService.updateBalance]:', error);
      throw error;
    }
  },

  /**
   * Get student behavior count (street cents)
   */
  async getStudentBehaviorCount(studentId: string): Promise<{ positive: number; negative: number }> {
    try {
      const {
        data,
        count: positiveCount,
        error: positiveError,
      } = await supabase.rpc('get_behavior_counts', { student_id: studentId });

      console.log({ data, count: positiveCount, error: positiveError });
      return data as { positive: number; negative: number };
    } catch (error) {
      console.error('[StudentService.getStudentBehaviorCount]:', error);
      throw error;
    }
  },

  /**
   * Get monthly balance history
   */
  async getMonthlyBalance(studentId?: string): Promise<Array<{ month: string; balance: number }>> {
    try {
      const { data, error } = await supabase.rpc('get_student_monthly_balances', { student_id: studentId });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('[StudentService.getMonthlyBalance]:', error);
      throw error;
    }
  },

  /**
   * Get student financial summary
   */
  async getStudentFinancialSummary(studentId: string): Promise<{
    currentBalance: number;
    totalCredit: number;
    totalDebit: number;
  }> {
    try {
      const { data, error } = await supabase
        .rpc('get_student_financial_summary', { input_student_id: studentId })
        .single();

      if (error) throw error;

      console.log('[StudentService.getStudentFinancialSummary]:', data);

      return {
        currentBalance: data.current_balance,
        totalCredit: data.total_credit,
        totalDebit: data.total_debit,
      };
    } catch (error) {
      console.error('[StudentService.getStudentFinancialSummary]:', error);
      throw error;
    }
  },

  /**
   * Update profile picture for a student
   */
  async updateProfilePicture(id: string, file: File): Promise<string> {
    try {
      // Attempt to upload new image to Supabase storage
      let { data, error } = await supabase.storage
        .from('profile-pictures')
        .upload(`public/${id}/${file.name}`, file, { upsert: true });
      console.log('id:', id);
      console.log('file:', file);

      // Handle duplicate error by removing existing file and retrying upload
      if (error && error.statusCode === '409') {
        await supabase.storage.from('profile-pictures').remove([`public/${id}/${file.name}`]);
        const retry = await supabase.storage.from('profile-pictures').upload(`public/${id}/${file.name}`, file);
        if (retry.error) throw retry.error;
        data = retry.data;
      }

      if (error) throw error;

      // Construct the public URL
      const publicUrl = supabase.storage.from('profile-pictures').getPublicUrl(data.path).data.publicUrl;
      console.log('Profile picture updated successfully', publicUrl);

      // Update the student's profile picture URL in the database
      const { error: updateError } = await supabase.from('users').update({ photourl: publicUrl }).eq('id', id);

      if (updateError) throw updateError;

      return publicUrl;
    } catch (error) {
      console.error('Error updating profile picture:', error);
      throw error;
    }
  },

  /**
   * Delete profile picture for a student
   */
  async deleteProfilePicture(id: string): Promise<void> {
    try {
      // Fetch the existing profile picture URL
      const { data: existingData, error: fetchError } = await supabase
        .from('users')
        .select('photourl')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // If a profile picture exists, remove it from storage
      if (existingData?.photourl) {
        const path = existingData.photourl.replace(
          supabase.storage.from('profile-pictures').getPublicUrl('').data.publicUrl,
          ''
        );
        await supabase.storage.from('profile-pictures').remove([path]);
      }

      // Update the student's profile picture URL in the database to null
      const { error: updateError } = await supabase.from('users').update({ photourl: null }).eq('id', id);

      if (updateError) throw updateError;
    } catch (error) {
      console.error('Error deleting profile picture:', error);
      throw error;
    }
  },

  /**
   * Get total virtual currency across active students, with optional teacher/cohort scoping
   */
  async getTotalVirtualCurrency(filters?: {
    teacherId?: string;
    teacherUserId?: string;
    cohortId?: string;
  }): Promise<{
    totalBalance: number;
    totalCredit: number;
    totalDebit: number;
  }> {
    try {
      const scopedStudentIds = await resolveScopedStudentIds(filters);
      if (scopedStudentIds !== null && scopedStudentIds.length === 0) {
        return { totalBalance: 0, totalCredit: 0, totalDebit: 0 };
      }

      let query = supabase
        .from('transactions')
        .select(
          `
            amount,
            status,
            transaction_type:transaction_types!inner(type),
            student:students!inner(
              id,
              user:users!inner(
                id,
                deletedat
              )
            )
          `
        )
        .is('deletedat', null)
        .is('student.user.deletedat', null);

      if (scopedStudentIds !== null) {
        query = query.in('studentid', scopedStudentIds);
      }

      const { data, error } = await query;
      if (error) throw error;

      return calculateTransactionTotals(data || []);
    } catch (error) {
      console.error('[StudentService.getTotalVirtualCurrency]:', error);
      throw error;
    }
  },

  /**
   * Get platform monthly balances for active students, with optional teacher/cohort scoping
   */
  async getPlatformMonthlyBalances(filters?: {
    teacherId?: string;
    teacherUserId?: string;
    cohortId?: string;
  }): Promise<{ month: string; balance: number }[]> {
    try {
      const scopedStudentIds = await resolveScopedStudentIds(filters);
      if (scopedStudentIds !== null && scopedStudentIds.length === 0) {
        return [];
      }

      let query = supabase
        .from('transactions')
        .select(
          `
            amount,
            createdat,
            status,
            transaction_type:transaction_types!inner(type),
            student:students!inner(
              id,
              user:users!inner(
                id,
                deletedat
              )
            )
          `
        )
        .is('deletedat', null)
        .is('student.user.deletedat', null)
        .order('createdat', { ascending: true });

      if (scopedStudentIds !== null) {
        query = query.in('studentid', scopedStudentIds);
      }

      const { data, error } = await query;
      if (error) throw error;

      return aggregateMonthlyBalances(data || []);
    } catch (error) {
      console.error('[StudentService.getPlatformMonthlyBalances]:', error);
      throw error;
    }
  },

  /**
   * Get top performing active students
   */
  async getTopPerformingStudents(
    options?:
      | number
      | {
          limit?: number;
          teacherId?: string;
          teacherUserId?: string;
          cohortId?: string;
        }
  ): Promise<{
    count: number;
    data: {
      id: string;
      firstname: string;
      lastname: string;
      email: string;
      grade: number;
      balance: number;
      positive_behaviors: number;
      negative_behaviors: number;
    }[];
  }> {
    try {
      const { limit, teacherId, teacherUserId, cohortId } = parseTopPerformingStudentOptions(options);

      const scopedStudentIds = await resolveScopedStudentIds({ teacherId, teacherUserId, cohortId });
      if (scopedStudentIds !== null && scopedStudentIds.length === 0) {
        return { count: 0, data: [] };
      }

      let query = supabase
        .from(TABLE_NAME)
        .select(
          `
            id,
            grade,
            balance,
            user:users!inner(
              id,
              firstname,
              lastname,
              email,
              deletedat
            )
          `,
          { count: 'exact' }
        )
        .is('user.deletedat', null);

      if (scopedStudentIds !== null) {
        query = query.in('id', scopedStudentIds);
      }

      const { data, count, error } = await query
        .order('balance', { ascending: false })
        .limit(limit);

      if (error) throw error;

      const formattedData = (data || []).map((s: any) => ({
        id: s.id,
        firstname: s.user?.firstname || '',
        lastname: s.user?.lastname || '',
        email: s.user?.email || '',
        grade: s.grade,
        balance: Number(s.balance) || 0,
        positive_behaviors: 0,
        negative_behaviors: 0,
      }));

      return {
        count: count || 0,
        data: formattedData,
      };
    } catch (error) {
      console.error('[StudentService.getTopPerformingStudents]:', error);
      throw error;
    }
  },

  // getStudentByUserId
  async getStudentByUserId(userId: string): Promise<string | null> {
    try {
      const { data, error } = await supabase.from(TABLE_NAME).select('id').eq('userid', userId).single();

      if (error) throw error;
      return data.id;
    } catch (error) {
      console.error('[StudentService.getStudentByUserId]:', error);
      throw error;
    }
  },
};
